import { z } from 'zod';
import { AutonomyLevel, Role } from '../enums';

/**
 * Catálogo de regras de política. Cada regra tem schema de valor, valor padrão
 * global e os modos de enforcement aceitos.
 *
 * Enforcement:
 *  - DEFAULT: camadas inferiores podem sobrescrever livremente
 *  - LOCKED: mandatória; não pode ser alterada por camadas inferiores
 *  - FLOOR: camadas inferiores só podem definir valor >= (numéricas)
 *  - CEILING: camadas inferiores só podem definir valor <= (numéricas)
 */
export const Enforcement = { DEFAULT: 'DEFAULT', LOCKED: 'LOCKED', FLOOR: 'FLOOR', CEILING: 'CEILING' } as const;
export type Enforcement = (typeof Enforcement)[keyof typeof Enforcement];

interface RuleDef<S extends z.ZodTypeAny> {
  schema: S;
  default: z.infer<S>;
  numeric: boolean;
  label: string;
  description: string;
}

const def = <S extends z.ZodTypeAny>(d: RuleDef<S>) => d;

const roleEnum = z.enum(Object.values(Role) as [Role, ...Role[]]);

export const RULE_CATALOG = {
  'checkin.enabled': def({
    schema: z.boolean(),
    default: true,
    numeric: false,
    label: 'Check-ins periódicos',
    description: 'Abre check-ins automáticos para o estudante na periodicidade definida (desligado: o acompanhamento é feito pelas etapas de entrega).',
  }),
  'checkin.frequencyDays': def({
    schema: z.number().int().min(7).max(120),
    default: 15,
    numeric: true,
    label: 'Periodicidade do check-in (dias)',
    description: 'Intervalo entre check-ins do estudante (quinzenal = 15, mensal = 30).',
  }),
  'checkin.reminderOffsetsDays': def({
    schema: z.array(z.number().int().min(0).max(30)).max(5),
    default: [0, 3],
    numeric: false,
    label: 'Lembretes de check-in (dias após o prazo)',
    description: 'Dias, contados do prazo do check-in, em que o estudante recebe lembrete (proposta: no dia e 3 dias depois).',
  }),
  'checkin.missedAlertAfterDays': def({
    schema: z.number().int().min(1).max(30),
    default: 4,
    numeric: true,
    label: 'Alerta de check-in sem resposta (dias após o prazo)',
    description: 'Após este número de dias sem resposta, o check-in é marcado como sem resposta e a orientação é alertada.',
  }),
  'milestone.alertDaysBefore': def({
    schema: z.array(z.number().int().min(1).max(365)).max(4),
    default: [60],
    numeric: false,
    label: 'Alerta de marcos institucionais (dias antes)',
    description: 'Antecedência dos alertas de marcos institucionais (qualificação, defesa, fim de bolsa).',
  }),
  'tasks.maxNewPerCycle': def({
    schema: z.number().int().min(1).max(20),
    default: 5,
    numeric: true,
    label: 'Máximo de tarefas novas por ciclo',
    description: 'Limite de tarefas publicadas ao estudante por ciclo de check-in (proposta: 3 a 5).',
  }),
  'extension.approverRoles': def({
    schema: z.array(roleEnum).min(1),
    default: ['LEAD_ADVISOR', 'COORDINATOR', 'ADMIN_INSTITUTION'] as Role[],
    numeric: false,
    label: 'Papéis que aprovam prorrogações',
    description: 'Papéis autorizados a aprovar prorrogações formais de prazo.',
  }),
  'extension.maxDays': def({
    schema: z.number().int().min(0).max(730),
    default: 180,
    numeric: true,
    label: 'Prorrogação máxima por checkpoint (dias)',
    description: 'Diferença máxima entre o prazo original e o prazo prorrogado.',
  }),
  'upload.maxSizeMb': def({
    schema: z.number().int().min(1).max(500),
    default: 25,
    numeric: true,
    label: 'Tamanho máximo de arquivo (MB)',
    description: 'Limite por arquivo enviado (o servidor também aplica UPLOAD_MAX_MB).',
  }),
  'upload.allowedExtensions': def({
    schema: z.array(z.string().regex(/^[a-z0-9]{1,10}$/)).min(1),
    default: ['pdf', 'docx', 'odt', 'txt', 'md', 'csv', 'xlsx', 'pptx', 'png', 'jpg', 'jpeg', 'zip', 'tex', 'bib', 'py', 'r', 'ipynb', 'json'],
    numeric: false,
    label: 'Extensões permitidas',
    description: 'Extensões aceitas em entregas e evidências (o MIME real também é verificado).',
  }),
  'ai.enabled': def({
    schema: z.boolean(),
    default: false,
    numeric: false,
    label: 'IA assistiva habilitada',
    description: 'Permite solicitar análises de IA (exige também configuração do servidor).',
  }),
  'work.defaultAutonomy': def({
    schema: z.enum([AutonomyLevel.FULL_REVIEW, AutonomyLevel.AUTO_LOW_RISK]),
    default: AutonomyLevel.FULL_REVIEW as AutonomyLevel,
    numeric: false,
    label: 'Nível de autonomia padrão',
    description: 'FULL_REVIEW: tudo passa pela orientação; AUTO_LOW_RISK: tarefas de baixo risco são publicadas automaticamente.',
  }),
  'digest.weeklyEnabled': def({
    schema: z.boolean(),
    default: true,
    numeric: false,
    label: 'Resumo semanal à orientação',
    description: 'Envia às segundas, 08:00 (timezone da instituição), quem está em risco, o que venceu e o que vence em 14 dias.',
  }),
  'data.retentionYears': def({
    schema: z.number().int().min(1).max(50),
    default: 5,
    numeric: true,
    label: 'Retenção de arquivos após conclusão (anos)',
    description: 'Prazo de retenção de arquivos de trabalhos concluídos (LGPD/regras do programa).',
  }),
} as const;

export type RuleKey = keyof typeof RULE_CATALOG;
export type RuleValue<K extends RuleKey> = z.infer<(typeof RULE_CATALOG)[K]['schema']>;
export type RuleValues = { [K in RuleKey]: RuleValue<K> };
export const RULE_KEYS = Object.keys(RULE_CATALOG) as RuleKey[];

export function isRuleKey(k: string): k is RuleKey {
  return Object.prototype.hasOwnProperty.call(RULE_CATALOG, k);
}

export function defaultRuleValues(): RuleValues {
  const out = {} as Record<string, unknown>;
  for (const k of RULE_KEYS) out[k] = structuredClone(RULE_CATALOG[k].default);
  return out as RuleValues;
}
